import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, RefreshCw, LogOut, Mail, Phone, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ys_admin_token";

const STATUSES = ["new", "contacted", "quoted", "won", "closed"];

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = useCallback(
    () => ({ headers: { "X-Admin-Token": token } }),
    [token]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quotes`, {
        headers: { "X-Admin-Token": token },
      });
      setQuotes(res.data);
      setAuthed(true);
      localStorage.setItem(STORAGE_KEY, token);
    } catch (e) {
      if (e.response?.status === 401) {
        setAuthed(false);
        localStorage.removeItem(STORAGE_KEY);
        toast.error("Invalid admin token");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = (e) => {
    e.preventDefault();
    setToken(input.trim());
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}`, { status }, headers());
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/quotes/${id}`, headers());
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setToken("");
    setInput("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-border p-10"
        >
          <div className="font-display uppercase text-2xl text-white flex items-center gap-2 mb-2">
            <span className="w-2.5 h-2.5 bg-acid" /> Pure Space
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-8">
            Admin — Quote Requests
          </p>
          <input
            data-testid="admin-token-input"
            type="password"
            placeholder="Admin token"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="w-full bg-transparent border-b border-border text-white py-4 focus:outline-none focus:border-acid transition-colors mb-8"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            className="w-full bg-acid text-obsidian py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-white transition-colors"
          >
            Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <header className="border-b border-border px-6 lg:px-12 h-20 flex items-center justify-between">
        <div className="font-display uppercase text-lg flex items-center gap-2">
          <span className="w-2.5 h-2.5 bg-acid" /> Quote Requests
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-[0.15em] hover:border-acid hover:text-acid transition-colors"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-[0.15em] hover:border-acid hover:text-acid transition-colors"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="px-6 lg:px-12 py-10">
        <div className="mb-8 flex items-baseline gap-4">
          <span className="font-display text-5xl text-acid">{quotes.length}</span>
          <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">
            Total requests
          </span>
        </div>

        {quotes.length === 0 ? (
          <div className="border border-border p-16 text-center text-zinc-500 uppercase tracking-[0.2em] text-sm">
            No requests yet
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-quote-list">
            {quotes.map((q) => (
              <div
                key={q.id}
                data-testid={`admin-quote-${q.id}`}
                className="border border-border p-6 bg-surface"
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="font-display text-2xl uppercase tracking-tight">
                      {q.name}
                    </div>
                    {q.company ? (
                      <div className="text-sm text-zinc-500 flex items-center gap-2 mt-1">
                        <Building2 size={14} /> {q.company}
                      </div>
                    ) : null}
                  </div>
                  <button
                    data-testid={`admin-delete-${q.id}`}
                    onClick={() => remove(q.id)}
                    className="text-zinc-600 hover:text-red-400 transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                <div className="space-y-2 text-sm text-zinc-300 mb-4">
                  <a
                    href={`mailto:${q.email}`}
                    className="flex items-center gap-2 hover:text-acid transition-colors"
                  >
                    <Mail size={14} /> {q.email}
                  </a>
                  <a
                    href={`tel:${q.phone}`}
                    className="flex items-center gap-2 hover:text-acid transition-colors"
                  >
                    <Phone size={14} /> {q.phone}
                  </a>
                </div>

                <div className="flex flex-wrap gap-2 mb-4 text-[11px] uppercase tracking-[0.15em]">
                  <span className="border border-acid text-acid px-3 py-1">
                    {q.service_type}
                  </span>
                  {q.property_size ? (
                    <span className="border border-border text-zinc-400 px-3 py-1">
                      {q.property_size}
                    </span>
                  ) : null}
                  {q.location ? (
                    <span className="border border-border text-zinc-400 px-3 py-1">
                      {q.location}
                    </span>
                  ) : null}
                </div>

                {q.message ? (
                  <p className="text-sm text-zinc-400 font-light border-l-2 border-border pl-4 mb-4">
                    {q.message}
                  </p>
                ) : null}

                <div className="flex items-center justify-between border-t border-border pt-4">
                  <div className="flex flex-wrap gap-1">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        onClick={() => updateStatus(q.id, s)}
                        className={`text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 transition-colors ${
                          q.status === s
                            ? "bg-acid text-obsidian font-bold"
                            : "text-zinc-500 hover:text-white"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <span className="text-[10px] text-zinc-600">
                    {new Date(q.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
